import {APP_API_BASE as API_BASE} from './appApiConfig';

const TOKEN_KEY = 'vocabmaster_auth_token';

function getAuthHeader() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? {Authorization: `Bearer ${token}`} : {};
}

async function requestJson(path, options = {}) {
  const authHeaders = getAuthHeader();
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options.headers || {}),
    },
    ...options,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with status ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

// Hebrew verbs API endpoints

function normalizeVerbForm(form = {}) {
  const tenseGroup = form.tense_group ?? form.tenseGroup ?? form.tense ?? '';
  return {
    ...form,
    tense_group: tenseGroup,
    tense: form.tense ?? tenseGroup,
    form_he: form.form_he ?? form.form ?? '',
    form_he_plain: form.form_he_plain ?? '',
    audio_url: form.audio_url ?? form.audioUrl ?? '',
    voice: form.voice ?? 'active',
    person: form.person ?? '',
    number_gender: form.number_gender ?? form.numberGender ?? '',
    meaning: form.meaning ?? '',
    stressIndex: form.stress_index ?? form.stressIndex ?? -1,
  };
}

function normalizeVerb(verb = {}) {
  return {
    ...verb,
    infinitive_he: verb.infinitive_he ?? verb.hebrewInf ?? '',
    infinitive_en: verb.infinitive_en ?? verb.englishInf ?? '',
    pealim_id: verb.pealim_id ?? verb.pealimId ?? '',
    sample_sentences: verb.sample_sentences ?? verb.sampleSentences ?? '',
    forms: Array.isArray(verb.forms) ? verb.forms.map(normalizeVerbForm) : [],
  };
}

function normalizeVerbList(result) {
  if (Array.isArray(result)) {
    return {
      verbs: result.map(normalizeVerb),
      total: result.length,
      page: 1,
      limit: result.length,
    };
  }

  return {
    ...result,
    verbs: Array.isArray(result?.verbs) ? result.verbs.map(normalizeVerb) : [],
    total: result?.total ?? result?.verbs?.length ?? 0,
  };
}

function normalizeProgress(result) {
  if (!Array.isArray(result)) return result || {};
  return result.reduce((acc, item) => {
    acc[item.formId] = item;
    return acc;
  }, {});
}

export async function fetchHebrewVerbs({search = '', root = '', binyan = '', rootLetter = '', page = 1, limit = 50}) {
  const query = new URLSearchParams();
  if (search && search.trim()) query.set('search', search.trim());
  if (root && root.trim()) query.set('root', root.trim());
  if (binyan && binyan.trim()) query.set('binyan', binyan.trim());
  if (rootLetter && rootLetter.trim()) query.set('rootLetter', rootLetter.trim());
  query.set('page', page.toString());
  query.set('limit', limit.toString());
  
  return normalizeVerbList(await requestJson(`/api/hebrew-verbs?${query.toString()}`));
}

export async function fetchHebrewVerbDetail(verbId) {
  const result = await requestJson(`/api/hebrew-verbs/${encodeURIComponent(verbId)}`);
  return {
    ...result,
    verb: normalizeVerb(result?.verb),
    forms: Array.isArray(result?.forms) ? result.forms.map(normalizeVerbForm) : [],
  };
}

export async function importHebrewVerbFromPealim(pealimUrl) {
  return requestJson('/api/hebrew-verbs/import', {
    method: 'POST',
    body: JSON.stringify({pealimUrl}),
  });
}

export async function fetchVerbDrillProgress() {
  return normalizeProgress(await requestJson('/api/hebrew-verbs/progress'));
}

export async function saveVerbDrillProgress({verbFormId, correct}) {
  return requestJson('/api/hebrew-verbs/progress', {
    method: 'POST',
    body: JSON.stringify({formId: verbFormId, correct}),
  });
}

export async function fetchAvailableBinyans() {
  return requestJson('/api/hebrew-verbs/binyans');
}

export async function fetchAvailableRoots({search = ''}) {
  const query = new URLSearchParams();
  if (search) query.set('search', search);
  return requestJson(`/api/hebrew-verbs/roots?${query.toString()}`);
}

export async function updateHebrewVerbMnemonic({verbId, mnemonic, binyanHint}) {
  return requestJson(`/api/hebrew-verbs/${encodeURIComponent(verbId)}/mnemonic`, {
    method: 'PATCH',
    body: JSON.stringify({mnemonic, binyanHint}),
  });
}

export async function updateHebrewVerbSentences({verbId, sentences}) {
  return requestJson(`/api/hebrew-verbs/${encodeURIComponent(verbId)}/sentences`, {
    method: 'PATCH',
    body: JSON.stringify({sentences}),
  });
}

export async function fetchDrillCards({mode = 'inf-to-form', limit = 100} = {}) {
  const query = new URLSearchParams();
  query.set('mode', mode);
  query.set('limit', String(limit));
  return requestJson(`/api/hebrew-verbs/drill-cards?${query.toString()}`);
}
